import React, { useEffect, useMemo, useState } from 'react';
import { QuestionRendererProps } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, ArrowRight, Lightbulb } from 'lucide-react';
import { StepSolverConfig, StepSolverStep } from '../../../content/types';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { NUMERIC_TOLERANCE } from '../../../content/questions/validators/shared';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
export function StepSolverRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = (question.data?.stepSolver || question.data || {}) as StepSolverConfig;
  const steps: StepSolverStep[] = config.steps || [];
  const context = config.context || '';
  const showStepsProgressively = config.showStepsProgressively ?? true;
  const [userSteps, setUserSteps] = useState<Record<number, string>>({});
  const [revealedHints, setRevealedHints] = useState<Record<number, boolean>>({});
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  useEffect(() => {
    setUserSteps({});
    setRevealedHints({});
    setCurrentStepIndex(0);
  }, [question.id]);
  useEffect(() => {
    // Ready when all steps are filled
    const allFilled = steps.every((_, i) => userSteps[i] && userSteps[i].trim() !== '');
    onReadyChange?.(allFilled);
  }, [userSteps, steps.length, onReadyChange]);
  const answerPayload = useMemo(() => JSON.stringify(userSteps), [userSteps]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  const handleInputChange = (index: number, value: string) => {
    if (shouldValidate) return;
    // R7-DID3: Filter non-numeric characters for numeric steps
    const step = steps[index];
    const accepted = step.acceptedAnswers || [step.correctAnswer];
    const isNumericStep = accepted.every((ans: string | number) => {
      const normalized = String(ans).replace(/[€\s,]/g, '.').trim();
      return normalized !== '' && !isNaN(parseFloat(normalized));
    });
    const filtered = isNumericStep ? value.replace(/[^0-9.,-€\s]/g, '') : value;
    setUserSteps(prev => ({
      ...prev,
      [index]: filtered
    }));
  };
  const handleNextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  };
  const isStepCorrect = (index: number) => {
    const step = steps[index];
    const userVal = userSteps[index]?.trim() || '';
    const accepted = step.acceptedAnswers || [step.correctAnswer];
    return accepted.some(ans => {
      const acceptedStr = String(ans).trim();
      if (acceptedStr.toLowerCase() === userVal.toLowerCase()) return true;
      const userNum = parseFloat(userVal.replace(/[€\s,]/g, '.'));
      const acceptedNum = parseFloat(acceptedStr.replace(/[€\s,]/g, '.'));
      if (!isNaN(userNum) && !isNaN(acceptedNum)) {
        return Math.abs(userNum - acceptedNum) < NUMERIC_TOLERANCE;
      }
      return false;
    });
  };
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-6" data-id="element-11025">
      {context && <div className="p-4 bg-indigo-50 text-indigo-900 rounded-xl border border-indigo-100 font-medium" data-id="element-11026">
          {context}
        </div>}

      <div className="flex flex-col gap-4" data-id="element-11027">
        <AnimatePresence initial={false} data-id="element-11028">
          {steps.map((step, index) => {
          const isVisible = !showStepsProgressively || index <= currentStepIndex;
          if (!isVisible) return null;
          const isFilled = userSteps[index] && userSteps[index].trim() !== '';
          const isCurrent = index === currentStepIndex;
          const isPast = index < currentStepIndex;
          let validationClass = 'border-slate-200 bg-white';
          let ValidationIcon = null;
          if (shouldValidate) {
            const correct = isStepCorrect(index);
            validationClass = correct ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50';
            ValidationIcon = correct ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" data-id="element-11029" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" data-id="element-11030" />;
          }
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`p-5 rounded-2xl border-2 transition-all shadow-sm flex flex-col gap-3 ${validationClass} ${isCurrent && !shouldValidate ? 'ring-4 ring-indigo-100 border-indigo-300' : ''}`} data-id="element-11031">
                <div className="flex items-center justify-between" data-id="element-11032">
                  <div className="flex items-center gap-3" data-id="element-11033">
                    <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center font-bold text-sm" data-id="element-11034">
                      {index + 1}
                    </div>
                    <h3 className="font-bold text-slate-800 text-lg" data-id="element-11035">
                      {step.prompt}
                    </h3>
                  </div>
                  {ValidationIcon}
                </div>

                <div className="flex items-center gap-3 mt-2" data-id="element-11036">
                  <Input value={userSteps[index] || ''} onChange={e => handleInputChange(index, e.target.value)} disabled={shouldValidate || isPast && showStepsProgressively} placeholder="Typ je antwoord..." className="flex-1 text-lg font-medium" autoFocus={isCurrent} onKeyDown={e => {
                if (e.key === 'Enter' && isFilled && !shouldValidate) {
                  handleNextStep();
                }
              }} data-id="element-11037" />

                  {showStepsProgressively && isCurrent && !shouldValidate && index < steps.length - 1 && <Button onClick={handleNextStep} disabled={!isFilled} variant="primary" className="shrink-0" data-id="element-11038">
                        Volgende <ArrowRight className="w-4 h-4 ml-2" data-id="element-11039" />
                      </Button>}
                </div>

                {shouldValidate && !isStepCorrect(index) && <div className="text-sm font-bold text-red-600 mt-1 bg-red-100/50 p-2 rounded-lg inline-block" data-id="element-11040">
                    Correct antwoord: {step.correctAnswer}
                  </div>}

                {step.hint && isCurrent && !shouldValidate && <div className="mt-2" data-id="element-11041">
                    {!revealedHints[index] ? <button onClick={() => setRevealedHints(prev => ({
                ...prev,
                [index]: true
              }))} className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-indigo-600 transition-colors bg-slate-100 hover:bg-indigo-50 px-3 py-1.5 rounded-full" data-id="element-11042">
                        <Lightbulb className="w-4 h-4" data-id="element-11043" />
                        Hint
                      </button> : <div className="text-sm text-slate-600 italic bg-slate-50 p-3 rounded-xl border border-slate-200" data-id="element-11044">
                        💡 {step.hint}
                      </div>}
                  </div>}
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </div>;
}